import { useMemo } from 'react';
import { chartRows, type Surface, type Tooth } from '@oxyflows/domain';
import { useI18n } from '../i18n';

/**
 * The interactive odontogram (spec §6).
 *
 * The clinically critical property: the chart is drawn from the CLINICIAN'S
 * point of view facing the patient, so the patient's RIGHT appears on the LEFT.
 * Column order comes from `chartRows()` in packages/domain, which derives it
 * from the FDI quadrant alone — it has no locale or direction input, so
 * switching the interface to Arabic right-to-left cannot mirror the chart and
 * swap the patient's left and right.
 *
 * That is enforced here by `direction: ltr` on the arch rows: the surrounding
 * page mirrors, the mouth does not.
 */

export type ToothState = {
  toothFdi: string;
  status: string;
  kind: string;
  note: string | null;
};

export type SurfaceState = {
  toothFdi: string;
  surface: string;
  finding: string;
};

export interface OdontogramProps {
  dentition: 'permanent' | 'primary' | 'mixed';
  teeth: ToothState[];
  surfaces: SurfaceState[];
  imageCounts: Record<string, number>;
  selectedTooth: string | null;
  selectedSurfaces: Surface[];
  onSelectTooth: (fdi: string | null) => void;
  onToggleSurface: (surface: Surface) => void;
}

/** Status → colour and glyph. Status is never conveyed by colour alone. */
const STATUS_STYLE: Record<string, { fill: string; stroke: string; glyph: string; label: string }> = {
  sound: { fill: '#ffffff', stroke: '#c9d4dc', glyph: '', label: 'Sound' },
  caries: { fill: '#fdeceb', stroke: '#b3261e', glyph: '●', label: 'Caries' },
  restored: { fill: '#eef4fb', stroke: '#14548f', glyph: '▣', label: 'Restored' },
  crowned: { fill: '#fdf6e7', stroke: '#8a5a00', glyph: '⬢', label: 'Crown' },
  implant: { fill: '#eef8f8', stroke: '#0c6673', glyph: '⌷', label: 'Implant' },
  root_canal: { fill: '#f3eefb', stroke: '#5b3b9c', glyph: '⌁', label: 'Root canal' },
  missing: { fill: '#eef2f5', stroke: '#a9b6c0', glyph: '×', label: 'Missing' },
  unerupted: { fill: '#f7f9fa', stroke: '#c9d4dc', glyph: '◌', label: 'Unerupted' },
  retained_primary: { fill: '#fdf6e7', stroke: '#a9761f', glyph: '◑', label: 'Retained primary' },
  extracted: { fill: '#eef2f5', stroke: '#a9b6c0', glyph: '×', label: 'Extracted' },
  bridge_pontic: { fill: '#eef4fb', stroke: '#14548f', glyph: '≡', label: 'Pontic' },
  veneer: { fill: '#f0f8f4', stroke: '#0a7d4f', glyph: '▤', label: 'Veneer' },
  fractured: { fill: '#fdeceb', stroke: '#b3261e', glyph: '⚡', label: 'Fractured' },
  watch: { fill: '#fdf6e7', stroke: '#8a5a00', glyph: '◔', label: 'Watch' },
};

const SURFACE_FILL: Record<string, string> = {
  sound: '#ffffff',
  caries: '#e4574c',
  restoration: '#4a90c2',
  sealant: '#63b892',
  fracture: '#b3261e',
  wear: '#c9a227',
  discolouration: '#9b8579',
  watch: '#e0b84a',
};

export function Odontogram(props: OdontogramProps) {
  const { t, locale } = useI18n();
  const rows = useMemo(() => chartRows(props.dentition), [props.dentition]);

  const byTooth = useMemo(() => {
    const m = new Map<string, ToothState>();
    for (const s of props.teeth) m.set(s.toothFdi, s);
    return m;
  }, [props.teeth]);

  const bySurface = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of props.surfaces) m.set(`${s.toothFdi}:${s.surface}`, s.finding);
    return m;
  }, [props.surfaces]);

  return (
    <div className="odontogram">
      <div className="odontogram-legend">
        <span className="odontogram-side">
          {locale === 'ar' ? 'يمين المريض' : "Patient's right"}
        </span>
        <span className="odontogram-arch-label">{locale === 'ar' ? 'الفك العلوي' : 'Upper'}</span>
        <span className="odontogram-side">
          {locale === 'ar' ? 'يسار المريض' : "Patient's left"}
        </span>
      </div>

      {/*
        `dir="ltr"` is load-bearing, not cosmetic. Everything else on the page
        mirrors in Arabic; the mouth must not, or the patient's left and right
        would swap (spec §6).
      */}
      <div className="odontogram-arch" dir="ltr">
        {rows.upper.map((tooth) => (
          <ToothCell
            key={tooth.fdi}
            tooth={tooth}
            state={byTooth.get(tooth.fdi)}
            bySurface={bySurface}
            imageCount={props.imageCounts[tooth.fdi] ?? 0}
            selected={props.selectedTooth === tooth.fdi}
            selectedSurfaces={props.selectedTooth === tooth.fdi ? props.selectedSurfaces : []}
            onSelect={props.onSelectTooth}
            onToggleSurface={props.onToggleSurface}
          />
        ))}
      </div>

      <div className="odontogram-midline" aria-hidden="true" />

      <div className="odontogram-arch" dir="ltr">
        {rows.lower.map((tooth) => (
          <ToothCell
            key={tooth.fdi}
            tooth={tooth}
            state={byTooth.get(tooth.fdi)}
            bySurface={bySurface}
            imageCount={props.imageCounts[tooth.fdi] ?? 0}
            selected={props.selectedTooth === tooth.fdi}
            selectedSurfaces={props.selectedTooth === tooth.fdi ? props.selectedSurfaces : []}
            onSelect={props.onSelectTooth}
            onToggleSurface={props.onToggleSurface}
          />
        ))}
      </div>

      <div className="odontogram-legend" style={{ marginTop: 10 }}>
        <span className="odontogram-arch-label">{locale === 'ar' ? 'الفك السفلي' : 'Lower'}</span>
      </div>

      <div className="odontogram-key">
        {Object.entries(STATUS_STYLE)
          .filter(([k]) => k !== 'sound')
          .map(([key, style]) => (
            <span key={key} className="odontogram-key-item">
              <span
                className="odontogram-key-swatch"
                style={{ background: style.fill, borderColor: style.stroke }}
                aria-hidden="true"
              >
                {style.glyph}
              </span>
              {(t.chart.status as Record<string, string>)[key] ?? style.label}
            </span>
          ))}
      </div>
    </div>
  );
}

function ToothCell({
  tooth,
  state,
  bySurface,
  imageCount,
  selected,
  selectedSurfaces,
  onSelect,
  onToggleSurface,
}: {
  tooth: Tooth;
  state: ToothState | undefined;
  bySurface: Map<string, string>;
  imageCount: number;
  selected: boolean;
  selectedSurfaces: Surface[];
  onSelect: (fdi: string | null) => void;
  onToggleSurface: (s: Surface) => void;
}) {
  const style = STATUS_STYLE[state?.status ?? 'sound'] ?? STATUS_STYLE.sound!;
  const absent = state?.status === 'missing' || state?.status === 'extracted';

  return (
    <div className={`tooth${selected ? ' selected' : ''}`}>
      <button
        type="button"
        className="tooth-button"
        onClick={() => onSelect(selected ? null : tooth.fdi)}
        aria-pressed={selected}
        aria-label={`Tooth ${tooth.fdi}${state ? `, ${style.label}` : ''}`}
        title={state?.note ?? style.label}
      >
        <ToothSvg
          tooth={tooth}
          fill={style.fill}
          stroke={style.stroke}
          absent={absent}
          bySurface={bySurface}
          selectedSurfaces={selectedSurfaces}
          onToggleSurface={selected ? onToggleSurface : undefined}
        />
      </button>
      <span className="tooth-number">{tooth.fdi}</span>
      {style.glyph ? (
        <span className="tooth-glyph" aria-hidden="true">
          {style.glyph}
        </span>
      ) : null}
      {imageCount > 0 ? (
        <span className="tooth-images" title={`${imageCount} image(s)`}>
          {imageCount}
        </span>
      ) : null}
    </div>
  );
}

/**
 * A tooth drawn as its anatomical surfaces.
 *
 * Only the surfaces that actually exist on this tooth type are rendered: an
 * incisor gets an incisal edge, a molar an occlusal table. `surfacesFor()` is
 * the same function the API validates against, so the picture and the rules
 * can never disagree (spec §6).
 */
function ToothSvg({
  tooth,
  fill,
  stroke,
  absent,
  bySurface,
  selectedSurfaces,
  onToggleSurface,
}: {
  tooth: Tooth;
  fill: string;
  stroke: string;
  absent: boolean;
  bySurface: Map<string, string>;
  selectedSurfaces: Surface[];
  onToggleSurface?: (s: Surface) => void;
}) {
  const anterior = tooth.type === 'incisor' || tooth.type === 'canine';
  const centre = anterior ? 'incisal' : 'occlusal';
  const facial = anterior ? 'labial' : 'buccal';
  const lingual = tooth.arch === 'upper' ? 'palatal' : 'lingual';

  // Upper teeth: facial at the top. Lower teeth: facial at the bottom. That
  // keeps the drawing anatomically oriented in both arches.
  const top = tooth.arch === 'upper' ? facial : lingual;
  const bottom = tooth.arch === 'upper' ? lingual : facial;

  const surfaceProps = (s: string) => {
    const finding = bySurface.get(`${tooth.fdi}:${s}`);
    const isSelected = selectedSurfaces.includes(s as Surface);
    return {
      fill: finding ? SURFACE_FILL[finding] ?? fill : fill,
      stroke: isSelected ? '#141414' : stroke,
      strokeWidth: isSelected ? 2.5 : 1,
      className: onToggleSurface ? 'surface interactive' : 'surface',
      onClick: onToggleSurface
        ? (e: React.MouseEvent) => {
            e.stopPropagation();
            onToggleSurface(s as Surface);
          }
        : undefined,
    };
  };

  if (absent) {
    return (
      <svg viewBox="0 0 40 40" className="tooth-svg" role="img">
        <rect x="4" y="4" width="32" height="32" rx="5" fill="#eef2f5" stroke="#a9b6c0" strokeDasharray="3 2" />
        <path d="M12 12 L28 28 M28 12 L12 28" stroke="#8b9aa5" strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 40 40" className="tooth-svg" role="img">
      {/* Mesial is toward the midline; the domain layer already tells us which
          physical side that is via the quadrant, so we mirror by patient side. */}
      <polygon points="4,4 36,4 28,12 12,12" {...surfaceProps(top)} />
      <polygon points="36,4 36,36 28,28 28,12" {...surfaceProps(tooth.patientSide === 'right' ? 'mesial' : 'distal')} />
      <polygon points="4,36 36,36 28,28 12,28" {...surfaceProps(bottom)} />
      <polygon points="4,4 12,12 12,28 4,36" {...surfaceProps(tooth.patientSide === 'right' ? 'distal' : 'mesial')} />
      <rect x="12" y="12" width="16" height="16" {...surfaceProps(centre)} />
    </svg>
  );
}
